import { getProductRepository } from '../../commerce/product.repository';
import { cutoutUrl } from '../../commerce/cutouts';
import { resizedImageUrl } from '../../commerce/shopify.repository';
import type { ShopProduct } from '../../commerce/shopify.types';
import type { AisleConfig, SceneHotspot } from '../../scenes/scene.types';

export interface Aisle {
  element: HTMLElement;
  /** Raccourcis de catégorie, fixes à l'écran. */
  controls: HTMLElement;
  destroy(): void;
}

interface Bay {
  handle: string;
  label: string;
  element: HTMLElement;
  levels: HTMLElement;
  loaded: boolean;
  /** Position et largeur mesurées, jamais relues pendant le défilement. */
  left: number;
  width: number;
}

/**
 * Un bloc de l'allée et sa position, mesurée une fois. Le défilement ne doit
 * jamais relire la géométrie du DOM : c'est ce que faisait la parallaxe
 * précédente, à chaque événement et pour chaque baie.
 */
interface Block {
  element: HTMLElement;
  kind: 'bay' | 'passage';
  left: number;
  width: number;
}

/**
 * Vitesses relatives des plans de profondeur, exprimées en retard par rapport
 * au défilement : un plan lointain traîne, un plan proche devance. Les bornes
 * évitent qu'un bloc situé loin du centre parte à des dizaines de pixels.
 *
 * Réglage volontairement discret. Une première version, deux fois plus ample,
 * donnait trop de mouvement : le décor attirait l'œil davantage que les
 * produits. Seuls des plans qui peuvent honnêtement bouger sont animés, le fond
 * de vitrine, le reflet du verre et le couloir aperçu dans un passage. La
 * menuiserie, elle, est fixe.
 */
const PLANS = {
  fond: { facteur: 0.06, borne: 30, borneMobile: 16 },
  avant: { facteur: -0.09, borne: 34, borneMobile: 18 },
  lointain: { facteur: 0.13, borne: 46, borneMobile: 24 },
} as const;

function perLevel(config: AisleConfig, viewport: number): number {
  return viewport < 768 ? config.mobilePerLevel : config.perLevel;
}

/**
 * La Grande Allée : une étagère par catégorie, séparées par des passages de mur.
 *
 * On se déplace latéralement d'une étagère à l'autre, et verticalement à
 * l'intérieur d'une étagère, comme sur un rayonnage roulant. L'allée boucle :
 * une copie de la première étagère est posée à la fin et un saut invisible
 * ramène au début, si bien qu'on peut tourner indéfiniment.
 */
export interface AisleHandlers {
  /** Activation d'un produit ou d'une sortie. */
  onProduct: (hotspot: SceneHotspot, origine?: HTMLElement) => void;
  /** Survol d'un produit : la carte de repères s'affiche. */
  onHover?: (product: ShopProduct, anchor: HTMLElement) => void;
  onLeave?: () => void;
}

export function createAisle(
  config: AisleConfig,
  viewportElement: HTMLElement,
  handlers: AisleHandlers,
): Aisle {
  const onProduct = handlers.onProduct;
  const element = document.createElement('div');
  element.className = 'aisle';

  // La parallaxe est décorative et doit se taire quand le système le demande.
  // Le bouclage, lui, est une correction de position : il reste actif.
  const sansAnimation = window.matchMedia('(prefers-reduced-motion: reduce)');

  const controls = document.createElement('div');
  controls.className = 'aisle__controls';
  controls.setAttribute('role', 'group');
  controls.setAttribute('aria-label', 'Rejoindre une étagère');

  const bays: Bay[] = [];
  const blocks: Block[] = [];

  function createBay(collection: { handle: string; label: string }, clone: boolean): Bay {
    const bay = document.createElement('section');
    bay.className = 'aisle__bay';
    bay.dataset['handle'] = collection.handle;

    // Le nom de la collection est écrit dans le fronton de la vitrine. C'est du
    // texte, jamais une image : il doit rester traduisible, net à tout zoom et
    // lisible par un lecteur d'écran, qui s'en sert pour nommer la vitrine.
    const title = document.createElement('p');
    title.className = 'aisle__title';
    title.textContent = collection.label;

    if (clone) {
      // La copie de bouclage ne doit être ni annoncée, ni atteignable au clavier.
      bay.setAttribute('aria-hidden', 'true');
      bay.inert = true;
    } else {
      const titleId = `aisle-title-${collection.handle}`;
      title.id = titleId;
      bay.setAttribute('aria-labelledby', titleId);
    }

    const levels = document.createElement('div');
    levels.className = 'aisle__levels';

    const hint = document.createElement('p');
    hint.className = 'aisle__hint';
    hint.textContent = 'Faites glisser vers le haut';

    bay.append(title, levels, hint);

    const passage = document.createElement('div');
    passage.className = 'aisle__passage';
    passage.setAttribute('aria-hidden', 'true');

    element.append(bay, passage);
    blocks.push({ element: bay, kind: 'bay', left: 0, width: 0 });
    blocks.push({ element: passage, kind: 'passage', left: 0, width: 0 });
    return {
      handle: collection.handle,
      label: collection.label,
      element: bay,
      levels,
      loaded: false,
      left: 0,
      width: 0,
    };
  }

  for (const collection of config.collections) {
    bays.push(createBay(collection, false));

    const shortcut = document.createElement('button');
    shortcut.type = 'button';
    shortcut.className = 'aisle__shortcut';
    shortcut.textContent = collection.label;
    shortcut.addEventListener('click', () => goTo(collection.handle));
    controls.append(shortcut);
  }

  const exit = document.createElement('button');
  exit.type = 'button';
  exit.className = 'aisle__shortcut aisle__shortcut--exit';
  exit.textContent = config.exit.label;
  exit.addEventListener('click', () =>
    onProduct({
      id: 'galerie.exit',
      label: config.exit.label,
      action: 'navigate',
      target: config.exit.target,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    }),
  );
  controls.append(exit);

  /*
   * Copie de la première étagère : c'est elle qui rend la boucle invisible.
   *
   * Pas de boucle sur mobile. Le saut de défilement repositionne `scrollLeft`
   * en plein geste, et l'inertie tactile d'iOS reprend ensuite la main depuis
   * l'ancienne position : le rayon se bloquait ou sautait ailleurs. Sur un
   * écran étroit l'allée s'arrête donc à ses deux bouts, ce qui est banal et
   * lisible, plutôt que de tourner mal.
   */
  const boucle = window.innerWidth >= 768;
  const first = config.collections[0];
  const loopBay = boucle && first ? createBay(first, true) : null;
  if (loopBay) bays.push(loopBay);

  function markActive(handle: string): void {
    const buttons = controls.querySelectorAll<HTMLButtonElement>('.aisle__shortcut');
    config.collections.forEach((collection, index) => {
      const button = buttons[index];
      if (!button) return;
      const active = collection.handle === handle;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function goTo(handle: string): void {
    const bay = bays.find((candidate) => candidate.handle === handle && candidate.element.getAttribute('aria-hidden') !== 'true');
    if (!bay) return;
    // On vise le point d'accroche de l'étagère, son centre : viser son bord
    // gauche laissait le défilement s'arrêter sur un cran intermédiaire.
    const centre =
      bay.element.offsetLeft - (viewportElement.clientWidth - bay.element.offsetWidth) / 2;
    viewportElement.scrollTo({
      left: Math.max(0, centre),
      behavior: sansAnimation.matches ? 'auto' : 'smooth',
    });
  }

  function renderProducts(bay: Bay, products: ShopProduct[]): void {
    const size = perLevel(config, window.innerWidth);
    bay.levels.replaceChildren();

    for (let index = 0; index < products.length; index += size) {
      const level = document.createElement('ul');
      level.className = 'aisle__level';
      level.style.gridTemplateColumns = `repeat(${size}, 1fr)`;

      for (const product of products.slice(index, index + size)) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'aisle__product';
        button.setAttribute('aria-label', `${product.title}, ${product.price.amount} euros`);

        if (product.imageUrl) {
          const image = document.createElement('img');
          image.src = cutoutUrl(product.handle, resizedImageUrl(product.imageUrl, 320));
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.addEventListener('error', () => image.remove());
          button.append(image);
        }

        const caption = document.createElement('span');
        caption.className = 'aisle__caption';
        caption.textContent = product.title;
        button.append(caption);

        button.addEventListener('click', () =>
          onProduct(
            {
              id: `galerie.product.${product.handle}`,
              label: product.title,
              action: 'product',
              target: product.handle,
              x: 0,
              y: 0,
              width: 0,
              height: 0,
            },
            button,
          ),
        );

        // Le survol et le focus clavier montrent les mêmes repères.
        const montrer = (): void => handlers.onHover?.(product, button);
        const cacher = (): void => handlers.onLeave?.();
        button.addEventListener('pointerenter', montrer);
        button.addEventListener('focus', montrer);
        button.addEventListener('pointerleave', cacher);
        button.addEventListener('blur', cacher);

        item.append(button);
        level.append(item);
      }

      bay.levels.append(level);
    }

    if (products.length === 0) {
      const vide = document.createElement('p');
      vide.className = 'aisle__empty';
      vide.textContent = 'Étagère en cours de réassort.';
      bay.levels.append(vide);
    }
  }

  /** Passe à vrai quand la salle est quittée : les requêtes en vol s'arrêtent là. */
  let detruit = false;

  /** Une étagère ne charge son contenu que lorsqu'elle approche du champ. */
  async function fill(bay: Bay): Promise<void> {
    if (bay.loaded || detruit) return;
    bay.loaded = true;
    try {
      const page = await getProductRepository().getCollectionProducts(bay.handle, config.maxProducts);
      if (detruit) return;
      // Dans l'allée, on ne met en rayon que ce qui est réellement vendable :
      // les ruptures restent visibles en Réserve.
      const visibles = config.hideUnavailable
        ? page.products.filter((product) => product.availableForSale)
        : page.products;
      renderProducts(bay, visibles);
    } catch {
      bay.loaded = false;
      renderProducts(bay, []);
    }
  }

  /**
   * Géométrie mise en cache. Les largeurs de baie et de passage viennent du CSS
   * et ne dépendent pas du contenu : il suffit de mesurer au montage et à chaque
   * redimensionnement. Tout le reste du composant lit ce cache.
   */
  let largeurVue = 0;

  function mesurer(): void {
    largeurVue = viewportElement.clientWidth;
    for (const block of blocks) {
      block.left = block.element.offsetLeft;
      block.width = block.element.offsetWidth;
    }
    for (const bay of bays) {
      bay.left = bay.element.offsetLeft;
      bay.width = bay.element.offsetWidth;
    }
    calerLaTuile();
  }

  /**
   * Hauteur de la tuile de décor, en pixels, égale à la hauteur d'un niveau.
   *
   * Elle ne peut pas être écrite en pourcentage dans le CSS : le décor est posé
   * en `background-attachment: local` pour défiler avec les tablettes, et dans
   * ce mode un pourcentage se rapporte à la hauteur DÉFILABLE de la vitrine,
   * donc au nombre de produits de la collection. Une vitrine de six niveaux
   * affichait des tablettes deux fois trop espacées et les produits flottaient
   * entre deux verres.
   *
   * Le nombre de niveaux visibles est lu dans le CSS pour qu'il n'existe qu'à
   * un seul endroit.
   */
  function calerLaTuile(): void {
    const premiere = bays[0];
    if (!premiere) return;
    const visibles = Number.parseFloat(
      getComputedStyle(element).getPropertyValue('--aisle-visible-levels'),
    );
    const hauteur = premiere.levels.clientHeight;
    if (!hauteur || !Number.isFinite(visibles) || visibles <= 0) return;

    /*
     * Hauteur ARRONDIE au pixel, et la même pour la tuile et pour le niveau.
     *
     * Une hauteur fractionnaire, 188,33 px par exemple, faisait deux dégâts :
     * la répétition du décor accumulait des fractions de pixel et les tablettes
     * tremblaient pendant le défilement, surtout sur mobile ; et l'écart entre
     * la tuile et le niveau se creusait d'un niveau à l'autre.
     */
    const pas = Math.round(hauteur / visibles);
    element.style.setProperty('--aisle-tile-h', `${pas}px`);
    element.style.setProperty('--aisle-level-height', `${pas}px`);
  }

  function fillVisible(): void {
    const left = viewportElement.scrollLeft - largeurVue;
    const right = viewportElement.scrollLeft + largeurVue * 2;
    for (const bay of bays) {
      if (bay.left + bay.width >= left && bay.left <= right) void fill(bay);
    }
  }

  let lastScroll = 0;

  /**
   * Bouclage : arrivé sur la copie, on repart au début sans transition visible.
   * Le saut ne se déclenche qu'en fonction du sens du déplacement : au premier
   * affichage, `scrollLeft` vaut 0 sans que personne n'ait défilé vers la
   * gauche, et un saut à ce moment téléporte le visiteur en fin d'allée.
   */
  function loop(): void {
    if (!loopBay) return;
    const loopStart = loopBay.left;
    const firstBay = bays[0];
    if (!firstBay) return;

    const position = viewportElement.scrollLeft;
    const versLaDroite = position > lastScroll;

    if (versLaDroite && position >= loopStart - 2) {
      viewportElement.scrollLeft = firstBay.left + (position - loopStart);
    } else if (!versLaDroite && position <= 2 && lastScroll > 2) {
      viewportElement.scrollLeft = loopStart - largeurVue;
    }
    lastScroll = viewportElement.scrollLeft;
  }

  function currentBay(): Bay | undefined {
    const centre = viewportElement.scrollLeft + largeurVue / 2;
    return bays.find((bay) => centre >= bay.left && centre < bay.left + bay.width);
  }

  /**
   * Profondeur. Quatre plans glissent à des vitesses différentes autour du
   * centre de l'écran : le fond de vitrine traîne, les montants et le reflet
   * devancent, et le couloir aperçu dans un passage traîne davantage encore,
   * parce qu'il est le plus loin. C'est ce décalage qui fait qu'on longe une
   * rue au lieu de faire défiler une image.
   *
   * Aucune lecture du DOM ici : tout vient du cache posé par mesurer().
   */
  /** Remet tous les plans au neutre, sans quoi le décor resterait figé sur le
   *  dernier décalage écrit avant l'activation de la réduction d'animations. */
  function neutraliserPlans(): void {
    for (const block of blocks) {
      block.element.style.setProperty('--pan', '0px');
      block.element.style.setProperty('--pan-fg', '0px');
      block.element.style.setProperty('--pan-far', '0px');
    }
  }

  function profondeur(): void {
    if (sansAnimation.matches) {
      neutraliserPlans();
      return;
    }
    const centre = viewportElement.scrollLeft + largeurVue / 2;
    const mobile = largeurVue < 768;

    for (const block of blocks) {
      const ecart = block.left + block.width / 2 - centre;
      // Hors champ élargi, inutile de peindre : la valeur ne se verra pas.
      if (Math.abs(ecart) > largeurVue * 1.6) continue;

      const plan = block.kind === 'bay' ? PLANS.fond : PLANS.lointain;
      const borne = mobile ? plan.borneMobile : plan.borne;
      const fond = Math.max(-borne, Math.min(borne, ecart * plan.facteur));

      const borneAvant = mobile ? PLANS.avant.borneMobile : PLANS.avant.borne;
      const avant = Math.max(
        -borneAvant,
        Math.min(borneAvant, ecart * PLANS.avant.facteur),
      );

      const style = block.element.style;
      style.setProperty(block.kind === 'bay' ? '--pan' : '--pan-far', `${fond.toFixed(1)}px`);
      style.setProperty('--pan-fg', `${avant.toFixed(1)}px`);
    }
  }

  /**
   * Un seul rendu par image, quel que soit le nombre d'événements de
   * défilement. La version précédente exécutait tout le travail à chaque
   * événement, et relisait la géométrie de chaque baie au passage.
   */
  let planifie = false;

  function rendre(): void {
    planifie = false;
    loop();
    fillVisible();
    profondeur();
    const bay = currentBay();
    if (bay) markActive(bay.handle);
  }

  const onScroll = (): void => {
    if (planifie) return;
    planifie = true;
    requestAnimationFrame(rendre);
  };

  /**
   * Le contenu ne se recharge que si le nombre de produits par tablette change,
   * c'est-à-dire au passage de la bascule mobile. Vider les rayons à chaque
   * redimensionnement faisait disparaître les produits pendant le rechargement,
   * pour rien : une rotation d'écran ou la barre d'adresse d'un téléphone qui se
   * replie suffisaient à le déclencher, et un échec réseau laissait alors une
   * vitrine « en cours de réassort » qui était pleine une seconde plus tôt.
   */
  let parNiveau = perLevel(config, window.innerWidth);

  const onResize = (): void => {
    mesurer();
    const desormais = perLevel(config, window.innerWidth);
    if (desormais !== parNiveau) {
      parNiveau = desormais;
      for (const bay of bays) {
        bay.loaded = false;
        bay.levels.replaceChildren();
      }
    }
    fillVisible();
    profondeur();
  };

  // La réduction d'animations peut être activée pendant la visite : le décor
  // doit revenir au neutre sans attendre le prochain défilement.
  const onPreference = (): void => profondeur();

  viewportElement.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  sansAnimation.addEventListener('change', onPreference);

  const firstCollection = config.collections[0];
  if (firstCollection) markActive(firstCollection.handle);
  requestAnimationFrame(() => {
    mesurer();
    fillVisible();
    profondeur();
  });

  return {
    element,
    controls,
    destroy(): void {
      viewportElement.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      sansAnimation.removeEventListener('change', onPreference);
      // Une requête catalogue lente peut revenir après la sortie de la salle :
      // ce drapeau lui évite de peupler des noeuds déjà détachés.
      detruit = true;
      controls.remove();
      element.remove();
    },
  };
}
